"use client";

import { useState } from "react";
import { AlertTriangle, BellRing, BookOpen, FileText, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PROJECTS, chairOf, coversLine, health, issues, remindTarget, type Group } from "./data";
import { GroupDetail, ReportBody } from "./GroupDetail";
import { Dot, HealthBadge, PrimaryButton, Stat } from "./ui";

/**
 * The Governance Committee runs the governance projects: it sees how every
 * committee is doing, follows up with Chairs, reads their reports and puts the
 * board packet together.
 */
export function CommitteeView({ groups, reminded, onRemind, onRead }: {
    groups: Group[];
    reminded: string[];
    onRemind: (g: Group) => void;
    onRead: (g: Group) => void;
}) {
    const [open, setOpen] = useState<Group | null>(null);
    const [reading, setReading] = useState<Group | null>(null);
    const [packet, setPacket] = useState<"closed" | "confirm" | "done">("closed");

    const others = groups.filter((g) => g.id !== "gc");
    const counts = { "On track": 0, "Needs attention": 0, "Off track": 0 } as Record<string, number>;
    others.forEach((g) => { counts[health(g)]++; });
    const needYou = others.filter((g) => issues(g).length > 0).sort((a, b) => issues(b).length - issues(a).length);
    const reports = groups.filter((g) => g.report).sort((a, b) => Number(a.report!.read) - Number(b.report!.read));
    const unread = reports.filter((g) => !g.report!.read).length;
    const held = others.reduce((n, g) => n + g.held, 0);
    const onTime = others.reduce((n, g) => n + g.onTime, 0);
    const decisions = reports.filter((g) => g.report!.decisions !== "None");
    const missing = others.filter((g) => !g.report);

    const read = (g: Group) => { setReading(g); if (!g.report!.read) onRead(g); };

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-[#007a8a]">Governance Committee</div>
                    <h2 className="text-2xl font-bold text-[#003d45]">Governance projects</h2>
                    <p className="mt-1 text-sm text-gray-500">You look after the governance projects and help every committee follow them. Chapters and communities report through the Global Chapter Development Committee.</p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {PROJECTS.map((p) => (
                        <span key={p.name} title={p.note}
                            className={`rounded-full border px-3 py-1 text-xs font-medium ${p.status === "Live" ? "border-[#007a8a] bg-[#007a8a] text-white" : "border-gray-200 text-gray-400"}`}>
                            {p.name}{p.status !== "Live" && " · soon"}
                        </span>
                    ))}
                </div>
            </div>

            <div id="gov-gc-stats" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Stat label="Committees on track" value={`${counts["On track"]} of ${others.length}`} tone="green" />
                <Stat label="Need a follow-up" value={counts["Needs attention"] + counts["Off track"]} hint={`${counts["Off track"]} off track`} tone="amber" />
                <Stat label="Minutes confirmed on time" value={held ? `${Math.round((onTime / held) * 100)}%` : "—"} hint="Within 72 hours of the meeting" />
                <Stat label="Reports this quarter" value={`${others.length - missing.length} of ${others.length}`} hint={unread ? `${unread} unread` : "All read"} />
            </div>

            <div className="grid gap-6 lg:grid-cols-5">
                {/* Follow-ups */}
                <Card id="gov-gc-followups" className="lg:col-span-3">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-[#003d45]"><AlertTriangle className="size-4 text-amber-500" />Committees to follow up</CardTitle>
                        <CardDescription>A friendly reminder to the Chair is usually all it takes.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {needYou.length === 0 && <p className="text-sm text-gray-500">Every committee is on track.</p>}
                        {needYou.map((g) => (
                            <div key={g.id} className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-gray-200 p-3">
                                <div className="min-w-0">
                                    <button onClick={() => setOpen(g)} className="flex items-center gap-2 text-sm font-semibold text-gray-800 hover:underline">{g.name} <HealthBadge health={health(g)} /></button>
                                    <ul className="mt-1 space-y-0.5 text-xs text-gray-600">{issues(g).map((i) => <li key={i}>• {i}</li>)}</ul>
                                    <div className="mt-1 text-xs text-gray-400">Chair: {chairOf(g)}</div>
                                </div>
                                {reminded.includes(g.id)
                                    ? <span className="text-xs font-medium text-emerald-600">✓ Reminded today</span>
                                    : <Button variant="outline" size="sm" onClick={() => onRemind(g)}><BellRing />Remind {remindTarget(g).split(" ")[0]}</Button>}
                            </div>
                        ))}
                    </CardContent>
                </Card>

                {/* Reports */}
                <Card id="gov-gc-reports" className="lg:col-span-2">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-[#003d45]"><Mail className="size-4 text-[#007a8a]" />Reports from committees</CardTitle>
                        <CardDescription>Sent when a Chair confirms their minutes.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-1">
                        {reports.map((g) => (
                            <button key={g.id} onClick={() => read(g)} className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-gray-50">
                                <span className={`size-2 shrink-0 rounded-full ${g.report!.read ? "bg-transparent" : "bg-[#007a8a]"}`} />
                                <div className="min-w-0 flex-1">
                                    <div className={`truncate text-sm ${g.report!.read ? "text-gray-600" : "font-semibold text-gray-900"}`}>{g.name}</div>
                                    <div className="text-xs text-gray-400">Meeting {g.report!.meeting} · received {g.report!.received}</div>
                                </div>
                                <div className="flex gap-1">{g.report!.projects.map((p) => <Dot key={p.name} light={p.light} />)}</div>
                                {g.report!.decisions !== "None" && <FileText className="size-4 text-amber-500" aria-label="Asks for a decision" />}
                            </button>
                        ))}
                        <div className="border-t border-gray-100 pt-3">
                            <PrimaryButton id="gov-gc-packet" className="w-full" onClick={() => setPacket("confirm")}><BookOpen />Put together the board packet</PrimaryButton>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* All committees */}
            <Card id="gov-gc-groups">
                <CardHeader>
                    <CardTitle className="text-[#003d45]">Every committee</CardTitle>
                    <CardDescription>Click a committee to see its meetings, the policies it owns and its latest report.</CardDescription>
                </CardHeader>
                {/* Phones: one card per committee */}
                <CardContent className="space-y-2 md:hidden">
                    {groups.map((g) => (
                        <button key={g.id} onClick={() => setOpen(g)} className="w-full rounded-lg border border-gray-200 p-3 text-left">
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0"><div className="font-medium text-gray-800">{g.name}</div><div className="text-xs text-gray-400">{g.type} · {chairOf(g)}{g.covers && <span className="block text-[#007a8a]">{coversLine(g)}</span>}</div></div>
                                <HealthBadge health={health(g)} />
                            </div>
                            <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600">
                                <span>{g.held} held of {g.planned} planned{g.planned < 4 && <span className="text-red-600"> (min 4)</span>}</span>
                                <span>Minutes on time: {g.held ? `${g.onTime}/${g.held}` : "—"}</span>
                                <span className={g.nextYear === "Approved" ? "text-emerald-600" : "text-amber-600"}>2027: {g.nextYear}</span>
                                <span>Report: {g.report ? g.report.received : <span className="text-red-600">none</span>}</span>
                            </div>
                        </button>
                    ))}
                </CardContent>
                <CardContent className="hidden overflow-x-auto md:block">
                    <table className="w-full min-w-[640px] text-sm">
                        <thead><tr className="border-b border-gray-200 text-left text-xs text-gray-500"><th className="py-2 pr-3 font-medium">Committee</th><th className="pr-3 font-medium">Status</th><th className="pr-3 font-medium">Meetings this year</th><th className="pr-3 font-medium">Minutes on time</th><th className="pr-3 font-medium">2027 plan</th><th className="font-medium">Last report</th></tr></thead>
                        <tbody>
                            {groups.map((g) => (
                                <tr key={g.id} onClick={() => setOpen(g)} className="cursor-pointer border-b border-gray-100 hover:bg-gray-50">
                                    <td className="py-2.5 pr-3"><div className="font-medium text-gray-800">{g.name}</div><div className="text-xs text-gray-400">{g.type} · {chairOf(g)}{g.covers && <span className="block text-[#007a8a]">{coversLine(g)}</span>}</div></td>
                                    <td className="pr-3"><HealthBadge health={health(g)} /></td>
                                    <td className="pr-3 text-gray-600">{g.held} held of {g.planned || "0"} planned{g.planned < 4 && <span className="ml-1 text-xs text-red-600">(min 4)</span>}</td>
                                    <td className="pr-3 text-gray-600">{g.held ? `${g.onTime} of ${g.held}` : "—"}</td>
                                    <td className={`pr-3 ${g.nextYear === "Approved" ? "text-emerald-600" : "text-amber-600"}`}>{g.nextYear}</td>
                                    <td className="text-gray-600">{g.report ? g.report.received : <span className="text-red-600">None</span>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            <GroupDetail group={open} onClose={() => setOpen(null)} footer={(g) => issues(g).length > 0 && (
                reminded.includes(g.id) ? <p className="text-sm text-emerald-600">✓ {remindTarget(g)} was reminded today.</p>
                    : <PrimaryButton onClick={() => onRemind(g)}><BellRing />Remind {remindTarget(g)}</PrimaryButton>
            )} />

            <Dialog open={!!reading} onOpenChange={(o) => { if (!o) setReading(null); }}>
                <DialogContent className="sm:max-w-lg">
                    {reading?.report && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="text-[#003d45]">{reading.name}</DialogTitle>
                                <DialogDescription>Report from the {reading.report.meeting} meeting · confirmed by {chairOf(reading)}</DialogDescription>
                            </DialogHeader>
                            <ReportBody report={reading.report} />
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={packet !== "closed"} onOpenChange={(o) => { if (!o) setPacket("closed"); }}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-[#003d45]">{packet === "done" ? "Board packet ready" : "Board packet for Thu 12 Nov"}</DialogTitle>
                        <DialogDescription>{reports.length} committee reports{missing.length ? `, and a note of the ${missing.length} committees that haven't reported` : ""}.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div>
                            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#007a8a]">The Board is asked to decide</div>
                            {decisions.length === 0 ? <p className="text-gray-500">Nothing this time.</p>
                                : <ol className="list-decimal space-y-1 pl-5 text-gray-700">{decisions.map((g) => <li key={g.id}><b>{g.name}:</b> {g.report!.decisions}</li>)}</ol>}
                        </div>
                        {missing.length > 0 && (
                            <div>
                                <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#007a8a]">No report yet</div>
                                <p className="text-gray-700">{missing.map((g) => g.name).join(", ")}</p>
                            </div>
                        )}
                        {packet === "done" && <p className="font-medium text-emerald-600">✓ Sent to the Board Secretary for the 12 Nov meeting.</p>}
                    </div>
                    {packet === "confirm" && (
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setPacket("closed")}>Cancel</Button>
                            <PrimaryButton onClick={() => setPacket("done")}>Send to the Board Secretary</PrimaryButton>
                        </DialogFooter>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
